import React from 'react';
import { FileText, CheckCircle, XCircle } from 'lucide-react';

/**
 * TestSummaryTable Component
 * Displays a summary table showing attempted/unattempted questions
 * Section-wise if sections exist, overall always
 */
const TestSummaryTable = ({ testData, userAnswers }) => {
  // Calculate attempted and unattempted questions
  const calculateStats = (questions, answers, section = null) => {
    let total = 0;
    let attempted = 0;
    let unattempted = 0;

    questions.forEach((question, index) => {
      // If section is specified, check if question is in that section
      if (section) {
        const questionNum = index + 1;
        if (questionNum < section.startQuestion || questionNum > section.endQuestion) {
          return; // Skip questions not in this section
        }
      }

      total++;
      const questionId = question.id || `q${index}`;
      const answer = answers[questionId];
      
      if (answer !== null && answer !== undefined && answer !== '' && answer !== 'null' && answer !== 'undefined') {
        attempted++;
      } else {
        unattempted++;
      }
    });

    return { total, attempted, unattempted };
  };

  // Get overall stats
  const overallStats = calculateStats(testData.questions || [], userAnswers);

  // Get section-wise stats if sections exist
  const sections = testData.sections || [];
  const hasSections = testData.useDynamicSections && sections.length > 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <div className="flex items-center mb-4">
        <FileText className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Test Summary
        </h2>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700">
              <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                Section
              </th>
              <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">
                Total Questions
              </th>
              <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">
                Attempted
              </th>
              <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">
                Unattempted
              </th>
            </tr>
          </thead>
          <tbody>
            {hasSections ? (
              <>
                {/* Section-wise rows */}
                {sections.map((section, index) => {
                  const sectionStats = calculateStats(testData.questions || [], userAnswers, section);
                  return (
                    <tr key={section.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-sm text-gray-900 dark:text-white font-medium">
                        {section.name || `Section ${index + 1}`}
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-center text-sm text-gray-700 dark:text-gray-300">
                        {sectionStats.total}
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-center text-sm text-green-600 dark:text-green-400 font-medium">
                        {sectionStats.attempted}
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-center text-sm text-red-600 dark:text-red-400 font-medium">
                        {sectionStats.unattempted}
                      </td>
                    </tr>
                  );
                })}
                {/* Overall row */}
                <tr className="bg-blue-50 dark:bg-blue-900/20 font-semibold">
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-sm text-gray-900 dark:text-white">
                    Overall
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-center text-sm text-gray-900 dark:text-white">
                    {overallStats.total}
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-center text-sm text-green-600 dark:text-green-400">
                    {overallStats.attempted}
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-center text-sm text-red-600 dark:text-red-400">
                    {overallStats.unattempted}
                  </td>
                </tr>
              </>
            ) : (
              // Only overall row if no sections
              <tr className="bg-blue-50 dark:bg-blue-900/20 font-semibold">
                <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-sm text-gray-900 dark:text-white">
                  Overall
                </td>
                <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-center text-sm text-gray-900 dark:text-white">
                  {overallStats.total}
                </td>
                <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-center text-sm text-green-600 dark:text-green-400">
                  {overallStats.attempted}
                </td>
                <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-center text-sm text-red-600 dark:text-red-400">
                  {overallStats.unattempted}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
        <div className="flex items-center">
          <CheckCircle className="w-4 h-4 mr-1 text-green-600" />
          <span>Attempted</span>
        </div>
        <div className="flex items-center">
          <XCircle className="w-4 h-4 mr-1 text-red-600" />
          <span>Unattempted</span>
        </div>
      </div>
    </div>
  );
};

export default TestSummaryTable;

